import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { MaskedLine, EASE } from "./shared";
import { scrollToId } from "@/lib/scroll";
import { useLanguage } from "@/i18n/LanguageContext";

const HERO_BG =
    "https://images.unsplash.com/photo-1513346940221-6f673d962e97?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

export const Hero = ({ started }) => {
    const ref = useRef(null);
    const { t } = useLanguage();
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
    const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
    const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const glowX = useSpring(mx, { stiffness: 40, damping: 20 });
    const glowY = useSpring(my, { stiffness: 40, damping: 20 });

    const onMouseMove = (e) => {
        if (!ref.current) return;
        const r = ref.current.getBoundingClientRect();
        mx.set(e.clientX - r.left - r.width / 2);
        my.set(e.clientY - r.top - r.height / 2);
    };

    return (
        <section
            id="top"
            ref={ref}
            onMouseMove={onMouseMove}
            data-testid="hero-section"
            className="relative flex h-screen min-h-[680px] items-end overflow-hidden"
        >
            <motion.div style={{ y: bgY }} className="absolute inset-0 scale-[1.15]">
                <img
                    src={HERO_BG}
                    alt="Abstract streams of golden light on black"
                    className="h-full w-full object-cover opacity-70"
                />
            </motion.div>
            <div className="absolute inset-0 bg-black/60" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-black/40" />
            <motion.div
                style={{ x: glowX, y: glowY }}
                className="pointer-events-none absolute left-1/2 top-1/2 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold/10 blur-[120px]"
            />

            <motion.div
                style={{ opacity: contentOpacity, y: contentY }}
                className="relative z-10 mx-auto w-full max-w-[1400px] px-6 pb-24 md:px-12 md:pb-28"
            >
                <motion.p
                    data-testid="hero-overline"
                    initial={{ opacity: 0 }}
                    animate={started ? { opacity: 1 } : {}}
                    transition={{ duration: 1, delay: 0.9 }}
                    className="mb-8 flex items-center gap-4 text-[11px] uppercase tracking-[0.4em] text-gold"
                >
                    <span className="h-px w-12 bg-gold/60" />
                    {t.hero.overline}
                </motion.p>
                <h1 className="font-display text-[clamp(3.25rem,10vw,8rem)] font-medium leading-[0.95] tracking-tight">
                    <MaskedLine started={started} delay={0.35}>
                        <span data-testid="hero-line-1">{t.hero.line1}</span>
                    </MaskedLine>
                    <MaskedLine started={started} delay={0.5}>
                        <span data-testid="hero-line-2" className="italic text-gold">
                            {t.hero.line2}
                        </span>
                    </MaskedLine>
                    <MaskedLine started={started} delay={0.65}>
                        <span data-testid="hero-line-3">{t.hero.line3}</span>
                    </MaskedLine>
                </h1>
                <div className="mt-10 flex flex-col gap-10 md:flex-row md:items-end md:justify-between">
                    <motion.p
                        data-testid="hero-subtitle"
                        initial={{ opacity: 0, y: 24 }}
                        animate={started ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.9, ease: EASE, delay: 1.1 }}
                        className="max-w-md text-base leading-relaxed text-ash"
                    >
                        {t.hero.sub}
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={started ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.9, ease: EASE, delay: 1.25 }}
                        className="flex flex-wrap items-center gap-4"
                    >
                        <button
                            data-testid="hero-cta-primary"
                            onClick={() => scrollToId("#contact")}
                            className="group flex items-center gap-3 bg-gold px-7 py-4 text-xs uppercase tracking-[0.2em] text-black transition-colors hover:bg-bone"
                        >
                            {t.hero.cta1}
                            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                        </button>
                        <button
                            data-testid="hero-cta-secondary"
                            onClick={() => scrollToId("#manifesto")}
                            className="border border-white/20 px-7 py-4 text-xs uppercase tracking-[0.2em] text-bone transition-colors hover:border-gold/60 hover:text-gold"
                        >
                            {t.hero.cta2}
                        </button>
                    </motion.div>
                </div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={started ? { opacity: 1 } : {}}
                transition={{ delay: 1.6, duration: 1 }}
                className="absolute bottom-8 right-6 z-10 hidden items-center gap-3 md:right-12 lg:flex"
            >
                <span className="text-[10px] uppercase tracking-[0.3em] text-ash">
                    {t.hero.scroll}
                </span>
                <ArrowDown className="h-4 w-4 animate-bounce text-gold" />
            </motion.div>
        </section>
    );
};
